/** A journal line as edited in a form: amounts are the raw input strings. */
export interface EditableLine {
    accountId: string;
    debit: string;
    credit: string;
    memo?: string;
}

/** Whole paisa, so sums of two-decimal amounts never drift. */
const toCents = (v: string | number | null | undefined): number => {
    const n = typeof v === 'number' ? v : parseFloat(v ?? '');
    return Number.isFinite(n) ? Math.round(n * 100) : 0;
};

export const sumLines = (lines: EditableLine[]): { debit: number; credit: number } => {
    const debit = lines.reduce((s, l) => s + toCents(l.debit), 0);
    const credit = lines.reduce((s, l) => s + toCents(l.credit), 0);
    return { debit: debit / 100, credit: credit / 100 };
};

/**
 * Mirrors the server's posting rules so Save can be disabled until they hold: at least two lines,
 * every line has an account and exactly one positive side, and debits equal credits.
 */
export const isBalanced = (lines: EditableLine[]): boolean => {
    if (lines.length < 2) return false;
    const valid = lines.every(l => {
        const d = toCents(l.debit);
        const c = toCents(l.credit);
        return !!l.accountId && d >= 0 && c >= 0 && (d > 0) !== (c > 0);
    });
    if (!valid) return false;
    const { debit, credit } = sumLines(lines);
    return debit > 0 && toCents(debit) === toCents(credit);
};

const formatter = new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export const formatMoney = (value: number | null | undefined): string => {
    const n = value ?? 0;
    return `${n < 0 ? '-' : ''}৳ ${formatter.format(Math.abs(n))}`;
};

export const today = (): string => {
    const d = new Date();
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

export const firstOfMonth = (): string => `${today().slice(0, 8)}01`;
